import { useState } from "react";
import { useWizard } from "@/stores/wizard";
import { useAuthStore } from "@/stores/auth";
import { api, ApiError } from "@/lib/api";
import { LabeledInput } from "./LabeledInput";
import { Checkbox } from "./Checkbox";

/**
 * Wizard step 2 for Drop collections — supply, price, time window, royalty,
 * baseURI, and the optional allowlist + reveal mechanics. Side-effect: when
 * the user picks a placeholder image we pin both the image and a JSON
 * wrapper (so the contract gets a single placeholderURI string).
 */
export function Step2DropConfig() {
  const w = useWizard();
  const isAuthed = useAuthStore((s) => s.isAuthed());
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadPlaceholder = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const { cid: imageCid } = await api.uploadFile(file);
      const { cid: jsonCid } = await api.uploadJson({
        name: `${w.name || "Collection"} — pre-reveal`,
        description: "Hidden until reveal.",
        image: `ipfs://${imageCid}`,
      });
      w.set("placeholderCid", jsonCid);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUploadError("Sign in to upload the placeholder.");
      } else {
        setUploadError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <Checkbox
          label="Open edition (no supply cap)"
          value={w.openEdition}
          onChange={(v) => w.set("openEdition", v)}
          hint="Anyone can keep minting until you close the window with mintEnd or pause the contract."
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {!w.openEdition && (
          <LabeledInput
            label="Max supply"
            value={w.maxSupply}
            onChange={(v) => w.set("maxSupply", Number(v))}
            type="number"
            min={1}
          />
        )}
        <LabeledInput label="Mint price (native token)" value={w.mintPriceEth} onChange={(v) => w.set("mintPriceEth", v)} placeholder="0.01" />
        <LabeledInput label="Mint start (local time)" value={w.mintStart} onChange={(v) => w.set("mintStart", v)} type="datetime-local" />
        <LabeledInput label="Mint end (optional)" value={w.mintEnd} onChange={(v) => w.set("mintEnd", v)} type="datetime-local" />
        <LabeledInput label="Max per wallet (0 = no limit)" value={w.maxPerWallet} onChange={(v) => w.set("maxPerWallet", Number(v))} type="number" min={0} />
        <LabeledInput label="Royalty (bps, e.g. 500 = 5%)" value={w.royaltyBps} onChange={(v) => w.set("royaltyBps", Number(v))} type="number" min={0} max={10000} />
      </div>
      <LabeledInput label="Royalty receiver" value={w.royaltyReceiver} onChange={(v) => w.set("royaltyReceiver", v)} placeholder="0x..." />
      <LabeledInput label="Base URI (ipfs://... with trailing slash)" value={w.baseUri} onChange={(v) => w.set("baseUri", v)} placeholder="ipfs://Qm.../" />

      <div className="rounded-md border border-border bg-muted/30 p-3">
        <div className="mb-2 flex items-center gap-2">
          <Checkbox
            label="Enable allowlist / presale"
            value={w.allowlistEnabled}
            onChange={(v) => w.set("allowlistEnabled", v)}
            hint="Lock public mint until a future timestamp. During presale only allowlisted wallets can mint, gated by an Atelier-issued signature. Manage the wallet list from the Dashboard after deploy."
          />
        </div>
        {w.allowlistEnabled && (
          <LabeledInput
            label="Public mint opens at (local time)"
            value={w.publicMintAt}
            onChange={(v) => w.set("publicMintAt", v)}
            type="datetime-local"
          />
        )}
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-3">
        <Checkbox
          label="Enable reveal mechanic"
          value={w.revealEnabled}
          onChange={(v) => w.set("revealEnabled", v)}
          hint="Show a single placeholder image for every minted token until you reveal the real art. Prevents trait-sniping during the mint window."
        />
        {w.revealEnabled && (
          <div className="mt-2 flex flex-col gap-1">
            <span className="label">Placeholder image (shown before reveal)</span>
            <input
              type="file"
              accept="image/*"
              disabled={!isAuthed || uploading}
              onChange={(e) => void uploadPlaceholder(e)}
            />
            {w.placeholderCid && (
              <span className="text-xs text-muted-foreground">cid: {w.placeholderCid}</span>
            )}
            {uploading && <span className="text-xs text-muted-foreground">Pinning…</span>}
            {uploadError && <span className="text-xs text-red-600">{uploadError}</span>}
            {!isAuthed && (
              <span className="text-xs text-muted-foreground">Sign in to upload.</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
